/**
 * Grid Layout Schema — data model, validation, and defaults for the
 * flexible grid layout engine.
 *
 * GridLayoutConfig → GridRow[] → GridCell[]
 *
 * Explicit rows (not auto-flow) give admins precise 2D control.
 * Each cell references a sectionType from the section registry.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const GRID_COLUMNS_MIN = 1
export const GRID_COLUMNS_MAX = 5
export const VALID_COLUMN_COUNTS = [1, 2, 3, 4, 5]

export const ROW_HEIGHT_OPTIONS = ['auto', 'fixed']

export const CELL_VARIANT_OPTIONS = [
  'default',
  'compact',
  'dark',
  'showcase',
  'gradient',
  'minimal',
]

// ---------------------------------------------------------------------------
// Factory functions
// ---------------------------------------------------------------------------

let _cellIdCounter = 0
let _rowIdCounter = 0

export function createCellId() {
  return `cell-${++_cellIdCounter}-${Date.now().toString(36)}`
}

export function createRowId() {
  return `row-${++_rowIdCounter}-${Date.now().toString(36)}`
}

export function createCell(sectionType, overrides = {}) {
  return {
    id: createCellId(),
    colSpan: 1,
    rowSpan: 1,
    sectionType,
    variant: 'default',
    hidden: false,
    responsive: null,
    config: {},
    ...overrides,
  }
}

export function createRow(cells = [], overrides = {}) {
  return {
    id: createRowId(),
    height: 'auto',
    minHeight: null,
    cells,
    ...overrides,
  }
}

export function createGridLayout(overrides = {}) {
  return {
    id: '',
    name: '',
    type: 'template',
    templateId: null,
    columns: 2,
    rows: [],
    theme: 'light',
    version: 1,
    gameType: null,
    ...overrides,
  }
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function validateCell(cell, columns) {
  const errors = []

  if (!cell.id) errors.push('Cell missing id')
  if (!cell.sectionType) errors.push('Cell missing sectionType')

  const span = cell.colSpan || 1
  if (span < 1 || span > columns) {
    errors.push(`colSpan ${span} out of range [1, ${columns}]`)
  }

  if (cell.colStart != null) {
    if (cell.colStart < 1 || cell.colStart > columns) {
      errors.push(`colStart ${cell.colStart} out of range [1, ${columns}]`)
    }
    if (cell.colStart + span - 1 > columns) {
      errors.push(`Cell overflows grid: colStart ${cell.colStart} + colSpan ${span} > ${columns}`)
    }
  }

  if (cell.rowSpan != null && (cell.rowSpan < 1 || cell.rowSpan > 10)) {
    errors.push(`rowSpan ${cell.rowSpan} out of range [1, 10]`)
  }

  return errors
}

export function validateRow(row, columns) {
  const errors = []

  if (!row.id) errors.push('Row missing id')
  if (!Array.isArray(row.cells)) {
    errors.push('Row missing cells array')
    return errors
  }

  // Check total colSpan doesn't exceed columns
  const totalSpan = row.cells.reduce((sum, c) => sum + (c.colSpan || 1), 0)
  if (totalSpan > columns) {
    errors.push(`Row total colSpan (${totalSpan}) exceeds grid columns (${columns})`)
  }

  // Check for overlapping cells (when colStart is explicit)
  const occupied = new Set()
  for (const cell of row.cells) {
    if (cell.colStart != null) {
      for (let c = cell.colStart; c < cell.colStart + (cell.colSpan || 1); c++) {
        if (occupied.has(c)) {
          errors.push(`Column ${c} occupied by multiple cells`)
        }
        occupied.add(c)
      }
    }
  }

  // Validate each cell
  for (const cell of row.cells) {
    errors.push(...validateCell(cell, columns))
  }

  return errors
}

export function validateGridLayout(layout) {
  const errors = []

  if (!layout) {
    return ['Layout is null or undefined']
  }

  if (!layout.id) errors.push('Layout missing id')
  if (!layout.name) errors.push('Layout missing name')

  const cols = layout.columns
  if (!VALID_COLUMN_COUNTS.includes(cols)) {
    errors.push(`Invalid column count: ${cols}. Must be one of ${VALID_COLUMN_COUNTS.join(', ')}`)
  }

  if (!Array.isArray(layout.rows)) {
    errors.push('Layout missing rows array')
    return errors
  }

  // Check for duplicate section types (each section should appear at most once)
  const sectionTypes = new Set()
  for (const row of layout.rows) {
    if (!row.cells) continue
    for (const cell of row.cells) {
      if (cell.sectionType && !cell.hidden) {
        if (sectionTypes.has(cell.sectionType)) {
          errors.push(`Duplicate section type: ${cell.sectionType}`)
        }
        sectionTypes.add(cell.sectionType)
      }
    }
  }

  for (const row of layout.rows) {
    errors.push(...validateRow(row, cols))
  }

  return errors
}

// ---------------------------------------------------------------------------
// Config migration — bump version when schema changes
// ---------------------------------------------------------------------------

const CURRENT_VERSION = 1

const migrations = {
  // Example: version 1 → 2 migration
  // 2: (config) => { config.newField = 'default'; return config },
}

export function migrateConfig(config) {
  let current = { ...config }
  let v = current.version || 0

  while (v < CURRENT_VERSION) {
    v++
    if (migrations[v]) {
      current = migrations[v](current)
    }
    current.version = v
  }

  return current
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get all section types used in a layout */
export function getUsedSectionTypes(layout) {
  const types = new Set()
  for (const row of layout.rows || []) {
    for (const cell of row.cells || []) {
      if (cell.sectionType) types.add(cell.sectionType)
    }
  }
  return types
}

/** Find the cell containing a given section type */
export function findCellBySectionType(layout, sectionType) {
  for (const row of layout.rows || []) {
    for (const cell of row.cells || []) {
      if (cell.sectionType === sectionType) return cell
    }
  }
  return null
}

/** Compute grid-template-columns CSS value */
export function getGridTemplateColumns(columns) {
  return `repeat(${columns}, minmax(0, 1fr))`
}
